import { Link2, Trash2, Video } from 'lucide-react'
import { Button, EmptyState, Input, Modal, Select, Textarea } from '../../../components/UI'

export default function ProductFormModal({
  isOpen,
  mode,
  saving,
  categories,
  categoryOptions,
  value,
  onClose,
  onProductChange,
  onVideoChange,
  onSubmit,
  onDeleteVideo
}) {
  if (!value) return null

  const title = mode === 'create' ? 'Nuevo producto' : 'Editar producto'
  const hasCategories = categories.length > 0
  const currentVideo = Array.isArray(value.product?.videos) && value.product.videos.length > 0
    ? value.product.videos[0]
    : null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} disabled={saving || !hasCategories}>
            {saving ? 'Guardando...' : mode === 'create' ? 'Crear producto' : 'Guardar cambios'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-6"
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
      >
        <section className="rounded-xl border border-gray-200 p-4">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-gray-900">Informacion general</h3>
            <p className="text-sm text-gray-500">Completa primero los datos base del producto.</p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Select
              label="Categoria"
              value={String(value.product.categoryId || '')}
              onChange={(event) => onProductChange('categoryId', event.target.value ? Number(event.target.value) : null)}
              options={[
                { value: '', label: hasCategories ? 'Selecciona una categoria' : 'Sin categorias disponibles' },
                ...categoryOptions
              ]}
            />
            <Input
              label="Nombre del producto"
              placeholder="Ej. Kit de herramientas"
              value={value.product.name || ''}
              onChange={(event) => onProductChange('name', event.target.value)}
            />
            <Input
              label="Slug"
              placeholder="kit-de-herramientas"
              value={value.product.slug || ''}
              onChange={(event) => onProductChange('slug', event.target.value)}
            />
            <Input
              label="Precio"
              type="number"
              min="0"
              step="0.01"
              placeholder="0"
              value={String(value.product.price ?? 0)}
              onChange={(event) => onProductChange('price', Number(event.target.value || 0))}
            />
            <Input
              label="Orden"
              type="number"
              value={String(value.product.sortOrder ?? 0)}
              onChange={(event) => onProductChange('sortOrder', Number(event.target.value || 0))}
            />
            <Select
              label="Estado"
              value={String(Boolean(value.product.isActive))}
              onChange={(event) => onProductChange('isActive', event.target.value === 'true')}
              options={[
                { value: 'true', label: 'Activo' },
                { value: 'false', label: 'Inactivo' }
              ]}
            />
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 p-4">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-gray-900">Contenido comercial</h3>
            <p className="text-sm text-gray-500">Describe el producto de forma clara para el catalogo.</p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <Textarea
              label="Descripcion"
              rows={4}
              placeholder="Describe de forma breve el producto."
              value={value.product.description || ''}
              onChange={(event) => onProductChange('description', event.target.value)}
            />
            <Textarea
              label="Presentacion"
              rows={4}
              placeholder="Agrega una presentacion comercial o beneficios."
              value={value.product.presentation || ''}
              onChange={(event) => onProductChange('presentation', event.target.value)}
            />
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 p-4">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-gray-900">Video del producto</h3>
            <p className="text-sm text-gray-500">Solo necesitas pegar la URL. El sistema detecta automaticamente el proveedor.</p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <Input
              label="URL del video"
              placeholder="https://www.youtube.com/watch?v=..."
              value={value.video.videoUrl || ''}
              onChange={(event) => onVideoChange('videoUrl', event.target.value)}
            />
          </div>

          {mode === 'edit' && (
            <div className="mt-6">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
                <Link2 className="h-4 w-4 text-primary-600" />
                Video registrado
              </div>
              {currentVideo ? (
                <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900">{currentVideo.title}</div>
                    <div className="text-xs text-gray-500">{currentVideo.sourceType}</div>
                    <div className="truncate text-xs text-gray-400">{currentVideo.videoUrl}</div>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<Trash2 className="h-4 w-4" />}
                    onClick={() => onDeleteVideo(currentVideo.id)}
                    disabled={saving}
                  >
                    Eliminar video
                  </Button>
                </div>
              ) : (
                <EmptyState
                  icon={Video}
                  title="Sin video registrado"
                  description="Este producto aun no tiene una URL de video guardada."
                  size="sm"
                />
              )}
            </div>
          )}
        </section>

        {!hasCategories && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Necesitas al menos una categoria antes de guardar productos.
          </div>
        )}
      </form>
    </Modal>
  )
}
